import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedinIn, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { CheckCircle } from "lucide-react";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { sendEvent } from "../utils/gtag";
import api from "../services/api";

const COMPANY_EMAILS = ["info@techvelly.com", "sales@techvelly.com"];

const categories = ["All", "Web App Development", "AI-Driven", "Mobile App Development", "Others"];

const CaseStudyPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const itemsPerPage = 6;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getPublic('projects');
        setProjects(data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCaseClick = (slug) => {
    navigate(`/case-studies/${slug}`);
  };

  const filtered =
    selectedCategory === "All"
      ? projects
      : projects.filter((p) => p.category === selectedCategory);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const displayed = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      sendEvent('contact_us_submit');
      setShowSuccessModal(true);
      setFormData({ name: '', company: '', email: '', phone: '', message: '' });

    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
      setErrorMessage("Submission failed. Please try again later.");
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-200 text-lg">
        Loading case studies...
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen flex flex-col bg-[#264864] text-white">
        <Header />

        {/* Hero Section */}
        <section className="relative py-20 px-6 text-center">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, #264864 0%, #35a89d 100%)",
            }}
          />
          <div className="relative z-10 max-w-4xl mx-auto">
            <h1 className="text-5xl font-extrabold mb-6 mt-4">Case Studies</h1>
            <p className="text-lg leading-relaxed opacity-90">
              Explore how we’ve partnered with clients to deliver impactful
              digital products — from AI-driven platforms to mobile and web
              applications.
            </p>
          </div>
        </section>

        {/* Filter Buttons */}
        <section className="py-6 px-6 bg-[#264864] text-center">
          <div className="flex justify-center gap-4 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedCategory(cat);
                  setCurrentPage(1);
                }}
                className={`px-5 py-2 rounded-md font-semibold transition ${
                  selectedCategory === cat
                    ? "bg-[#35a89d] text-white"
                    : "bg-white/10 hover:bg-white/20"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Case Study Grid */}
        <section className="py-10 px-6 bg-[#264864]">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
              {displayed.map((cs) => (
                <div
                  key={cs.id}
                  className="relative group rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1"
                  onClick={() => { sendEvent('case_study_read_more', { case_study_title: cs.title }); handleCaseClick(cs.slug); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleCaseClick(cs.slug)}
                >
                  <div
                    className="h-64 bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${cs.thumbnailUrl})` }}
                  >
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <button
                        className="px-5 py-2 rounded-md font-semibold bg-white/90 text-[#264864] hover:bg-white hover:shadow-lg transition"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          handleCaseClick(cs.slug);
                        }}
                      >
                        View Details
                      </button>
                    </div>
                    <div className="absolute bottom-0 w-full px-4 py-3 bg-[#35a89d]">
                      <h3 className="text-lg font-bold">{cs.title}</h3>
                      <p className="text-sm opacity-80">{cs.category}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-center mt-10 gap-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-4 py-2 rounded-md font-semibold transition ${
                    currentPage === i + 1
                      ? "bg-[#35a89d] text-white"
                      : "bg-white/10 hover:bg-white/20"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0f2a36] text-center">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-extrabold mb-6">
              Ready To Grow Your Brand Online?
            </h2>
            <button
              onClick={() => { sendEvent('request_quote_click'); navigate("/request-demo"); }}
              className="px-8 py-3 rounded-md font-semibold bg-[#35a89d] text-white hover:bg-[#2e897c] transition"
            >
              Request a Quote
            </button>
          </div>
        </section>

        {/* Contact Section */}
        <section
          className="py-20 relative"
          style={{
            background: "linear-gradient(90deg, #264864 0%, #35a89d 100%)",
          }}
        >
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left */}
              <div>
                <h3 className="text-xl font-semibold mb-3">TAKE THE SILK ROAD TO</h3>
                <h2 className="text-4xl font-extrabold mb-6">
                  Digitizing your Business Growth
                </h2>
                <p className="max-w-md mb-8 opacity-90">
                  Follow us for more updates or send us a message using the form.
                  We’ll get back to you shortly.
                </p>
                <ul className="max-w-2xl mx-auto mb-10 text-left space-y-4">
                  {[ 
                    "Access expert support whenever you need it.",
                    "Our specialists are fluent in 4+ languages so you can be sure communication will be smooth.",
                    "Spend less time waiting, our speedy support team typically responds in under 2 minutes.",
                    "Get personalized help from a team dedicated to your success.",
                  ].map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#35a89d] flex-shrink-0" />
                      <span className="text-lg text-white">{point}</span>
                    </li>
                  ))}
                </ul>
               <div className="flex gap-4">
                {[ 
                  {
                    icon: faLinkedinIn,
                    link: "https://www.linkedin.com/company/techvelly/",
                    isWhatsApp: false,
                  },
                  {
                    icon: faWhatsapp,
                    link: "https://wa.me/+8801927046394",
                    isWhatsApp: true,
                  },
                ].map((s, i) => (
                  <button
                    key={i}
                    onClick={() => window.open(s.link, "_blank")}
                    className="p-3 rounded-full border border-white/30 text-xl hover:bg-white/20 hover:scale-110 hover:shadow-lg transition transform"
                  >
                    <FontAwesomeIcon icon={s.icon} />
                  </button>
                ))}
              </div>

              </div>

              {/* Right */}
              <div
                className="rounded-xl p-8 shadow-lg backdrop-blur-md border border-white/20 hover:shadow-2xl hover:scale-[1.02] transition duration-300"
                style={{ background: "rgba(255,255,255,0.1)" }}
              >
                <h3 className="text-lg font-semibold mb-6 text-white">
                  Start a Conversation with Us
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5 text-white">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name*"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full border-b border-white/50 bg-transparent py-2 outline-none placeholder-white/70 focus:border-white transition"
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full border-b border-white/50 bg-transparent py-2 outline-none placeholder-white/70 focus:border-white transition"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email*"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full border-b border-white/50 bg-transparent py-2 outline-none placeholder-white/70 focus:border-white transition"
                  />
                  <input
                    type="text"
                    name="phone"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border-b border-white/50 bg-transparent py-2 outline-none placeholder-white/70 focus:border-white transition"
                  />
                  <textarea
                    rows={4}
                    name="message"
                    placeholder="Your Message*"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full border-b border-white/50 bg-transparent py-2 outline-none placeholder-white/70 focus:border-white transition"
                  />
                  <div className="text-right">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      onClick={() => sendEvent('contact_us_click')}
                      className="px-6 py-2 rounded-md font-semibold bg-[#264864] hover:bg-[#1f3a4f] hover:shadow-lg transition"
                    >
                      {isSubmitting ? 'Sending...' : 'Submit'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
      <ConfirmationModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success!"
        message="Your message has been sent successfully."
        type="success"
      />
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Submission Failed"
        message={errorMessage}
        type="error"
        companyEmails={COMPANY_EMAILS}
      />
    </>
  );
};

export default CaseStudyPage;